import axios from "axios";
import qs from "qs";
import { SPAYEE_BASE_API } from "../constants/env/internalService.constants";
import { ROOT_HOST_NEW, SPAYEE_URL } from "../constants/signInConstants";
import useSignUpStore from "../store/useSignUpStore";
import { OnboardingDestinationEnum } from "../types/common.types";
import { OTPVerificationResponse } from "../types/signup.types";
import { getSignupParamsFromURL } from "./signup";
import { extractOnboardingParamsFromLocalstorage } from "./storage.util";

const setIsFormLoading = useSignUpStore.getState().setIsFormLoading;

export const formPost = async ({
  url,
  payload,
}: {
  url: string;
  payload: object;
}) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await axios.post(url, qs.stringify(payload), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      resolve(response);
    } catch (error) {
      reject(error);
    }
  });
};

export const spayeeAuthenticateSignUp = ({
  domain,
  email,
  password,
}: {
  domain: string;
  email: string;
  password: string;
}) => {
  const form = document.getElementById(`spayee-signup-form`);

  let url = `https://${domain}/s/authenticate`;

  let { skipOnboarding, destination } =
    extractOnboardingParamsFromLocalstorage();

  if (skipOnboarding && destination) {
    let destinationPath = "";
    if (destination !== "") {
      switch (destination) {
        case OnboardingDestinationEnum.DigitalProducts:
          destinationPath = "/t/products";
          break;
        case OnboardingDestinationEnum.Courses:
          destinationPath = "/t/allcourses";
          break;
        case OnboardingDestinationEnum.Memberships:
          destinationPath = "/t/dashboard-membership";
          break;
        case OnboardingDestinationEnum.Webinars:
          destinationPath = "/t/live-sessions";
          break;
        case OnboardingDestinationEnum.TelegramCommunity:
          destinationPath = "/t/telegram";
          break;
        case OnboardingDestinationEnum.Home:
          destinationPath = "/t/home";
          break;
        case OnboardingDestinationEnum.PricingLaunchMonthly:
          destinationPath = "/t/upgrade?plan=launch&cycle=monthly";
          break;
        case OnboardingDestinationEnum.PricingLaunchYearly:
          destinationPath = "/t/upgrade?plan=launch&cycle=yearly";
          break;
        case OnboardingDestinationEnum.PricingRiseMonthly:
          destinationPath = "/t/upgrade?plan=rise&cycle=monthly";
          break;
        case OnboardingDestinationEnum.PricingRiseYearly:
          destinationPath = "/t/upgrade?plan=rise&cycle=yearly";
          break;
        case OnboardingDestinationEnum.PricingScaleMonthly:
          destinationPath = "/t/upgrade?plan=scale&cycle=monthly";
          break;
        case OnboardingDestinationEnum.PricingScaleYearly:
          destinationPath = "/t/upgrade?plan=scale&cycle=yearly";
          break;
      }
    }
    if (destinationPath) {
      url = `${url}?url=${encodeURIComponent(destinationPath)}&hideSecNav=true`;
    }
  }

  if (form && SPAYEE_URL && ROOT_HOST_NEW) {
    form.setAttribute("action", url);
    document
      .getElementById(`spayee-signup-form-email`)
      ?.setAttribute("value", email);
    document
      .getElementById(`spayee-signup-form-password`)
      ?.setAttribute("value", password);

    setTimeout(function () {
      (form as any).submit();
    }, 2000);
  }
};

export const spayeeAuthenticate = ({
  domain,
  email,
  code,
}: {
  domain: string;
  email: string;
  code: string;
}) => {
  return new Promise(async (resolve, reject) => {
    setIsFormLoading(true);
    try {
      const skipOnboardingParams = getSignupParamsFromURL();
      const response = await axios.post(
        `https://${domain}/s/authenticate/otp/verify?code=${encodeURIComponent(
          code
        )}&email=${encodeURIComponent(email)}&gweb=true`
      );

      if (response?.data) {
        setIsFormLoading(false);
        resolve(response?.data);
      } else if (response?.data?.response === false) {
        setIsFormLoading(false);
        reject(response?.data?.message);
      }
    } catch (error) {
      setIsFormLoading(false);
      reject(error);
    }
  });
};

export const otpAuthenticateOnSignUp = ({
  email,
  otp,
  phone,
  type,
  source,
}: {
  email: string;
  otp: string;
  phone: string;
  type: string;
  source?: string;
}): Promise<OTPVerificationResponse> => {
  return new Promise(async (resolve, reject) => {
    setIsFormLoading(true);
    try {
      // Build URL with parameters
      const params = new URLSearchParams({
        email: email,
        otp: otp,
        phone: phone,
        type: type
      });
      
      if (source) {
        params.append('source', source);
      }

      const url = `${SPAYEE_BASE_API}/s/admin/account/otp/verify?${params.toString()}`;

      const response = await axios.get(url);

      if (response?.data) {
        setIsFormLoading(false);
        resolve(response?.data as OTPVerificationResponse);
      } else if (response?.data?.response === false) {
        setIsFormLoading(false);
        reject({ response: false, message: response?.data?.message });
      }
    } catch (error: any) {
      setIsFormLoading(false);
      reject({ response: false, message: error?.response?.data?.message || error?.message });
    }
  });
};

export const spayeeLogin = ({
  email,
  token,
  setIsLoading,
  otpLogin,
}: {
  email: string;
  token: string;
  setIsLoading?: any;
  otpLogin: boolean;
}): Promise<string> => {
  const payloadObj = {
    email: email,
    password: "",
    token,
    otpLogin,
  };
  return new Promise(async (resolve, reject) => {
    let response: any = {};

    setIsFormLoading(true);
    try {
      response = await formPost({
        url: `${SPAYEE_BASE_API}/courseplatform/login/v1`,
        payload: payloadObj,
      });

      if (response?.data?.response) {
        setIsFormLoading(false);
        resolve(response?.data?.domain);
      } else if (!response?.data?.response) {
        if (setIsLoading) {
          setIsLoading(false);
        }
        setIsFormLoading(false);
        reject(response?.data?.message);
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const otpAuthenticate = async ({
  domain,
  emailId,
  password,
}: {
  domain: string;
  emailId: string;
  password?: string;
}) => {
  const form = document.getElementById(`spayee-social-form`);
  if (form) {
    form.setAttribute(
      "action",
      `https://${domain}/s/authenticate?password=${password}&email=${encodeURIComponent(
        emailId
      )}&gweb=true&age`
    );

    setTimeout(function () {
      (form as any).submit();
      console.log("sign in successful");
    }, 2000);
  }
};

export const spayeeOAuthenticate = async ({
  domain,
  socialToken,
  userId,
  type,
  setIsLoading,
}: {
  domain: string;
  socialToken: string;
  userId?: string;
  type: "google" | "fb";
  setIsLoading?: any;
}) => {
  const form = document.getElementById(`spayee-social-form`);
  if (form) {
    form.setAttribute("action", `https://${domain}/s/creator/social/login`);

    if (type === "google") {
      document
        .getElementById(`spayee-social-form-id_token`)
        ?.setAttribute("value", socialToken);

      document
        .getElementById(`spayee-social-form-type`)
        ?.setAttribute("value", "google");
    }

    setTimeout(function () {
      (form as any).submit();
      if (setIsLoading) {
        setIsLoading(false);
      }
    }, 2000);
  }
};
